import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Plus, MapPin, Wallet, Users, Briefcase, Star, CheckCircle2, MessageSquare, Upload, X, Trash2, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { useAuth } from "@/AuthContext";
import { useI18n } from "@/i18n";
import api, { apiError } from "@/api";
import { StarRating } from "@/components/CraftsmanCard";
import { ChatDialog } from "@/components/ChatDialog";
import { CreditsPanel } from "@/components/CreditsPanel";
import { ReportDialog } from "@/components/ReportDialog";


function StatusBadge({ status }) {
  const { t } = useI18n();
  const map = {
    open: "bg-[#F2E8DF] text-[#5A4B45]",
    assigned: "bg-[#E69625]/15 text-[#B27A1E]",
    completed: "bg-[#5C6646]/10 text-[#5C6646]",
  };
  return <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${map[status] || map.open}`}>{t(`status.${status || "open"}`)}</span>;
}

function ReviewDialog({ jobId, onDone }) {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const [rating, setRating] = useState(5);
  const [text, setText] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/jobs/${jobId}/review`, { rating, text });
      toast.success(t("review.success"));
      setOpen(false);
      onDone();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button size="sm" data-testid={`review-btn-${jobId}`} className="rounded-full bg-[#5C6646] hover:bg-[#4A5238] text-white gap-1.5"><Star className="w-4 h-4" />{t("dash.leaveReview")}</Button></DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle className="font-display">{t("review.title")}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="flex gap-1">
            {[1, 2, 3, 4, 5].map((n) => (
              <button type="button" key={n} data-testid={`star-${n}`} onClick={() => setRating(n)}>
                <Star className={`w-8 h-8 ${n <= rating ? "fill-[#E69625] text-[#E69625]" : "text-stone-300"}`} />
              </button>
            ))}
          </div>
          <div className="space-y-2"><Label>{t("review.comment")}</Label><Textarea data-testid="review-text" rows={3} value={text} onChange={(e) => setText(e.target.value)} className="rounded-lg" /></div>
          <Button data-testid="review-submit" type="submit" className="w-full rounded-full bg-[#C65338] hover:bg-[#A93F25] text-white">{t("review.submit")}</Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function CompleteDialog({ jobId, onDone, autoOpen = false }) {
  const { t } = useI18n();
  const [open, setOpen] = useState(autoOpen);
  const [rating, setRating] = useState(0);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (rating < 1) { toast.error(t("dash.pickStars")); return; }
    setBusy(true);
    try {
      await api.post(`/jobs/${jobId}/complete`, { rating, text });
      toast.success(t("review.success"));
      setOpen(false);
      onDone();
    } catch (err) { toast.error(apiError(err.response?.data?.detail) || err.message); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button data-testid={`complete-${jobId}`} className="rounded-full bg-[#5C6646] hover:bg-[#4A5238] text-white gap-2 h-11 px-6 font-bold shrink-0"><CheckCircle2 className="w-5 h-5" />{t("dash.doneBtn")}</Button></DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle className="font-display">{t("dash.completeTitle")}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <p className="text-sm text-[#5A4B45]">{t("dash.completeHint")}</p>
          <div className="flex gap-1">
            {[1, 2, 3, 4, 5].map((n) => (
              <button type="button" key={n} data-testid={`star-${n}`} onClick={() => setRating(n)}>
                <Star className={`w-9 h-9 ${n <= rating ? "fill-[#E69625] text-[#E69625]" : "text-stone-300"}`} />
              </button>
            ))}
          </div>
          <div className="space-y-2"><Label>{t("review.comment")}</Label><Textarea data-testid="review-text" rows={3} value={text} onChange={(e) => setText(e.target.value)} className="rounded-lg" /></div>
          <Button data-testid="complete-submit" type="submit" disabled={busy || rating < 1} className="w-full rounded-full bg-[#C65338] hover:bg-[#A93F25] text-white h-11 font-bold">{t("dash.completeSubmit")}</Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function JobSteps({ status, offers }) {
  const { t } = useI18n();
  const step = status === "completed" ? 3 : status === "assigned" ? 2 : 1;
  const items = [t("dash.step1"), t("dash.step2"), t("dash.step3")];
  return (
    <div data-testid="job-steps" className="flex items-center gap-2 mt-4 text-xs">
      {items.map((label, i) => {
        const n = i + 1; const done = n < step; const cur = n === step;
        return (
          <div key={label} className="flex items-center gap-2 flex-1 last:flex-none">
            <span className={`w-6 h-6 rounded-full flex items-center justify-center font-bold shrink-0 ${done ? "bg-[#5C6646] text-white" : cur ? "bg-[#C65338] text-white" : "bg-[#EBDDD3] text-[#8C7A70]"}`}>
              {done ? <CheckCircle2 className="w-4 h-4" /> : n}
            </span>
            <span className={`font-semibold ${cur ? "text-[#2D231F]" : "text-[#8C7A70]"}`}>{label}{n === 1 && offers > 0 ? ` (${offers})` : ""}</span>
            {n < 3 && <span className={`flex-1 h-0.5 rounded ${done ? "bg-[#5C6646]" : "bg-[#EBDDD3]"}`} />}
          </div>
        );
      })}
    </div>
  );
}

function Avatar({ name, src }) {
  const initials = (name || "?").split(" ").map((p) => p[0]).slice(0, 2).join("").toUpperCase();
  return src
    ? <img src={src} alt={name} className="w-14 h-14 rounded-full object-cover border border-[#EBDDD3] shrink-0" />
    : <span className="w-14 h-14 rounded-full bg-[#C65338]/10 text-[#C65338] font-display font-black text-lg flex items-center justify-center shrink-0">{initials}</span>;
}

function CustomerDashboard() {
  const { t, catName, money, jt, cityName } = useI18n();
  const [jobs, setJobs] = useState([]);
  const [apps, setApps] = useState({});
  const [chat, setChat] = useState(null);
  const [sp] = useSearchParams();
  const focusJob = sp.get("job");
  const wantComplete = sp.get("complete") === "1";

  const loadApps = async (jid) => {
    const { data } = await api.get(`/jobs/${jid}/applications`);
    setApps((p) => ({ ...p, [jid]: data }));
  };
  const load = () => api.get("/jobs/mine").then((r) => {
    setJobs(r.data);
    r.data.filter((j) => (j.applications || 0) > 0 || j.status !== "open").forEach((j) => loadApps(j._id));
  });
  useEffect(() => { load(); }, []); // eslint-disable-line
  useEffect(() => {
    if (!focusJob || !jobs.length) return;
    const el = document.querySelector(`[data-testid="my-job-${focusJob}"]`);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [focusJob, jobs.length]);

  const accept = async (jid, appId) => {
    if (!window.confirm(t("dash.acceptConfirm"))) return;
    await api.post(`/jobs/${jid}/accept`, { application_id: appId });
    toast.success(t("dash.accepted"));
    window.dispatchEvent(new Event("mm:unread"));
    load();
  };
  const deleteJob = async (jid) => {
    if (!window.confirm(t("dash.deleteConfirm"))) return;
    try {
      await api.delete(`/jobs/${jid}`);
      toast.success(t("dash.jobDeleted"));
    } catch (e) {
      toast.error(t("dash.cannotDelete"));
    }
    load();
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tight text-[#2D231F]">{t("dash.myJobs")}</h1>
        <Link to="/post-job"><Button data-testid="dash-post-job" className="rounded-full bg-[#C65338] hover:bg-[#A93F25] text-white gap-1.5"><Plus className="w-4 h-4" />{t("nav.postJob")}</Button></Link>
      </div>
      {jobs.length === 0 ? (
        <p className="text-[#A89A90] py-16 text-center">{t("dash.noJobs")}</p>
      ) : (
        <div className="space-y-6">
          {jobs.map((j) => {
            const offers = apps[j._id] || [];
            const priced = offers.filter((a) => a.price != null);
            const cheapestId = priced.length > 1 ? priced.reduce((m, a) => (a.price < m.price ? a : m))._id : null;
            const bestRated = offers.length > 1 ? offers.reduce((m, a) => ((a.rating || 0) > (m.rating || 0) ? a : m)) : null;
            const bestId = bestRated && (bestRated.rating || 0) > 0 ? bestRated._id : null;
            const showOffers = j.status === "open" ? (j.applications || 0) > 0 : offers.some((a) => a.status === "accepted");
            const visibleOffers = j.status === "open" ? offers : offers.filter((a) => a.status === "accepted");
            return (
            <div key={j._id} className={`rounded-2xl border bg-white p-6 shadow-sm ${focusJob === j._id ? "ring-4 ring-[#5C6646]/40 " : ""}${j.status === "open" && (j.applications || 0) > 0 ? "border-[#E69625]" : "border-[#EBDDD3]"}`} data-testid={`my-job-${j._id}`}>
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                  <div className="flex items-center gap-2"><h3 className="font-display font-bold text-xl text-[#2D231F]">{jt(j, "title")}</h3><StatusBadge status={j.status} /></div>
                  <div className="flex items-center gap-4 mt-2 text-sm text-[#8C7A70] flex-wrap">
                    <span className="inline-flex items-center gap-1"><MapPin className="w-4 h-4" />{cityName(j.city)}</span>
                    {j.budget != null && <span className="inline-flex items-center gap-1"><Wallet className="w-4 h-4" />{money(j.budget)}</span>}
                    <span className="px-2 py-0.5 rounded-full bg-[#5C6646]/10 text-[#5C6646] text-xs font-semibold">{catName(j.category)}</span>
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {j.status === "completed" && !j.reviewed && <ReviewDialog jobId={j._id} onDone={load} />}
                  {j.reviewed && <span className="text-xs text-[#5C6646] font-semibold">{t("dash.reviewed")} ✓</span>}
                  {j.status === "open" && (j.applications || 0) === 0 && (
                    <Button variant="ghost" size="sm" data-testid={`delete-job-${j._id}`} onClick={() => deleteJob(j._id)} className="rounded-full gap-1.5 text-[#B0413E]"><Trash2 className="w-4 h-4" />{t("dash.deleteJob")}</Button>
                  )}
                </div>
              </div>
              <JobSteps status={j.status} offers={j.applications || 0} />
              {j.status === "assigned" && (() => {
                const left = j.assigned_at ? Math.max(0, 30 - Math.floor((Date.now() - new Date(j.assigned_at).getTime()) / 86400000)) : null;
                return (
                  <div data-testid={`confirm-box-${j._id}`} className="mt-5 rounded-xl border-2 border-[#5C6646]/40 bg-[#5C6646]/10 px-5 py-4 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    <div>
                      <p className="font-display font-bold text-lg text-[#2D231F]">{t("dash.doneQ")}</p>
                      <p className="text-sm text-[#5A4B45] mt-1 max-w-xl">{t("dash.doneHint")}{left != null ? ` (${left === 0 ? t("dash.doneToday") : t("dash.doneDays").replace("{n}", left)})` : ""}.</p>
                    </div>
                    <CompleteDialog jobId={j._id} onDone={load} autoOpen={focusJob === j._id && wantComplete} />
                  </div>
                );
              })()}
              {j.status === "completed" && j.auto_completed && <p className="mt-3 text-xs text-[#8C7A70]">{t("dash.autoDone")}</p>}
              {j.status === "completed" && (
                <div data-testid={`again-box-${j._id}`} className="mt-5 rounded-xl border-2 border-[#C65338]/30 bg-[#C65338]/5 px-5 py-4 flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                  <div>
                    <p className="font-display font-bold text-lg text-[#2D231F]">{t("again.title")}</p>
                    <p className="text-sm text-[#5A4B45] mt-1">{t("again.sub")}</p>
                  </div>
                  <div className="flex flex-wrap gap-2 shrink-0">
                    {j.assigned_craftsman_id && <Link to={`/post-job?invite=${j.assigned_craftsman_id}&name=${encodeURIComponent(j.assigned_craftsman_name || "")}&city=${encodeURIComponent(j.city || "")}&cat=${encodeURIComponent(j.category || "")}`}><Button data-testid={`rehire-${j._id}`} className="rounded-full bg-[#C65338] hover:bg-[#A93F25] text-white h-10 px-5 font-bold">{t("again.same")}</Button></Link>}
                    <Link to={`/post-job?city=${encodeURIComponent(j.city || "")}`}><Button data-testid={`other-trade-${j._id}`} variant="outline" className="rounded-full h-10 px-5 font-bold border-2">{t("again.other")}</Button></Link>
                  </div>
                </div>
              )}
              {j.status === "open" && (j.applications || 0) === 0 && (
                <div className="mt-4 rounded-xl bg-[#FAF5F0] px-4 py-3 text-sm text-[#8C7A70]">
                  <p data-testid={`waiting-${j._id}`}>{t("dash.waiting")}</p>
                  <p className="mt-1 text-xs text-[#A89A90]">{t("dash.deleteHint")}</p>
                </div>
              )}
              {j.status === "open" && (j.applications || 0) > 0 && (
                <div data-testid={`offers-banner-${j._id}`} className="mt-5 rounded-xl bg-[#E69625]/15 border border-[#E69625]/40 px-4 py-3">
                  <p className="font-bold text-[#8A5A10] flex items-center gap-2"><Users className="w-4 h-4" />{j.applications} {j.applications === 1 ? t("jobs.application") : t("jobs.applications")} – {t("dash.offersBanner")}</p>
                  <p className="text-xs text-[#8A5A10]/80 mt-1">{t("dash.offersHint")}</p>
                </div>
              )}
              {showOffers && (
                <div className="mt-4 grid grid-cols-1 lg:grid-cols-2 gap-4">
                  {visibleOffers.map((a) => (
                    <div key={a._id} className={`rounded-2xl p-5 border flex flex-col ${a.status === "accepted" ? "bg-[#5C6646]/10 border-[#5C6646]" : "bg-[#FAF5F0] border-[#EBDDD3]"}`} data-testid={`offer-${a._id}`}>
                      <div className="flex items-start gap-4">
                        <Avatar name={a.craftsman_name} src={a.avatar} />
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-display font-bold text-lg text-[#2D231F]">{a.craftsman_name}</span>
                            {a.verified && <CheckCircle2 className="w-4 h-4 text-[#5C6646]" />}
                          </div>
                          <div className="text-xs text-[#8C7A70]">{a.trade ? catName(a.trade) : ""}{a.jobs_completed ? ` · ${a.jobs_completed} ${t("dash.jobsDone")}` : ""}</div>
                          <div className="mt-1"><StarRating value={a.rating} /></div>
                        </div>
                        {a.price != null && (
                          <div className="text-right shrink-0">
                            <div data-testid={`offer-price-${a._id}`} className="font-display font-black text-2xl text-[#C65338] leading-none">{money(a.price)}</div>
                            {cheapestId === a._id && <span className="inline-block mt-1.5 px-2 py-0.5 rounded-full bg-[#5C6646] text-white text-[10px] font-bold uppercase">{t("dash.cheapest")}</span>}
                          </div>
                        )}
                      </div>
                      {bestId === a._id && <span className="self-start mt-3 px-2 py-0.5 rounded-full bg-[#E69625] text-white text-[10px] font-bold uppercase">★ {t("dash.bestRated")}</span>}
                      {a.message && <p className="text-sm text-[#5A4B45] mt-3 whitespace-pre-wrap bg-white rounded-lg p-3 border border-[#EBDDD3]">{a.message}</p>}
                      <div className="flex items-center gap-2 mt-4 flex-wrap">
                        {j.status === "open" && <Button data-testid={`accept-${a._id}`} onClick={() => accept(j._id, a._id)} className="rounded-full bg-[#C65338] hover:bg-[#A93F25] text-white gap-1.5 h-10 px-6 font-bold"><CheckCircle2 className="w-4 h-4" />{t("dash.acceptOffer")}</Button>}
                        {a.status === "accepted" && <span className="text-sm text-[#5C6646] font-bold inline-flex items-center gap-1"><CheckCircle2 className="w-4 h-4" />{t("dash.accepted")}</span>}
                        {a.status === "accepted" && a.phone && <a href={`tel:${a.phone.replace(/[^0-9+]/g, "")}`} data-testid={`phone-${a._id}`} className="inline-flex items-center gap-1.5 rounded-full bg-[#5C6646] text-white text-sm font-bold px-4 h-9" title={t("dash.craftsmanPhone")}><Phone className="w-4 h-4" />{a.phone}</a>}
                        <Button size="sm" variant="outline" data-testid={`msg-${a._id}`} onClick={() => setChat({ jobId: j._id, cu: a.craftsman_user_id, name: a.craftsman_name })} className="rounded-full gap-1.5"><MessageSquare className="w-4 h-4" />{t("dash.message")}</Button>
                        {a.craftsman_id && <Link to={`/craftsmen/${a.craftsman_id}`} data-testid={`profile-${a._id}`} className="text-sm font-semibold text-[#5A4B45] underline underline-offset-2 hover:text-[#C65338]">{t("dash.viewProfile")}</Link>}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            );
          })}
        </div>
      )}
      {chat && <ChatDialog open={!!chat} onOpenChange={(o) => !o && setChat(null)} jobId={chat.jobId} craftsmanUserId={chat.cu} title={chat.name} />}
    </div>
  );
}

function CraftsmanDashboard({ view = "profile" }) {
  const { t, catName, money, jt, cityName } = useI18n();
  const { user } = useAuth();
  const [cats, setCats] = useState([]);
  const [cities, setCities] = useState([]);
  const [profile, setProfile] = useState(null);
  const [apps, setApps] = useState([]);
  const [reportedJobs, setReportedJobs] = useState([]);
  const [invited, setInvited] = useState([]);
  const [open, setOpen] = useState(false);
  const [chat, setChat] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef();
  const [form, setForm] = useState({ trade: "", bio: "", skills: "", city: "", hourly_rate: "", avatar: "", portfolio: [], phone: "" });

  const loadProfile = () => api.get("/craftsmen/me").then((r) => {
    setProfile(r.data);
    setForm({
      trade: r.data.trade || "", bio: r.data.bio || "", skills: (r.data.skills || []).join(", "),
      city: r.data.city || "", hourly_rate: r.data.hourly_rate ?? "", avatar: r.data.avatar || "",
      portfolio: r.data.portfolio || [], phone: r.data.phone || "",
    });
  });
  useEffect(() => {
    api.get("/categories").then((r) => setCats(r.data));
    api.get("/cities").then((r) => setCities(r.data));
    loadProfile();
    api.get("/applications/mine").then((r) => setApps(r.data));
    api.get("/reports/mine").then((r) => setReportedJobs(r.data || []));
    api.get("/jobs/invited").then((r) => setInvited(r.data || [])).catch(() => {});
  }, []);

  const uploadFiles = async (e, target) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      const urls = [];
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        urls.push(data.url);
      }
      if (target === "avatar") setForm((p) => ({ ...p, avatar: urls[0] }));
      else setForm((p) => ({ ...p, portfolio: [...p.portfolio, ...urls] }));
    } catch (err) { toast.error(apiError(err.response?.data?.detail) || "Upload failed"); }
    finally { setUploading(false); }
  };

  const save = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.put("/craftsmen/me", {
        trade: form.trade, bio: form.bio, city: form.city, avatar: form.avatar, portfolio: form.portfolio, phone: form.phone.trim(),
        hourly_rate: form.hourly_rate ? Number(form.hourly_rate) : null,
        skills: form.skills.split(",").map((s) => s.trim()).filter(Boolean),
      });
      setProfile(data); setOpen(false); toast.success(t("dash.saved"));
    } catch (err) { toast.error(apiError(err.response?.data?.detail) || err.message); }
  };

  return (
    <div className="grid grid-cols-1 gap-8">
      {view !== "offers" && <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
        <div className="rounded-xl border border-[#EBDDD3] bg-white p-6" data-testid="craftsman-profile-card">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-display text-xl font-bold text-[#2D231F]">{t("dash.profile")}</h2>
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild><Button variant="outline" size="sm" data-testid="edit-profile-btn" className="rounded-full">{t("dash.editProfile")}</Button></DialogTrigger>
              <DialogContent className="max-h-[85vh] overflow-y-auto">
                <DialogHeader><DialogTitle className="font-display">{t("dash.editProfile")}</DialogTitle></DialogHeader>
                <form onSubmit={save} className="space-y-4 pt-2">
                  <div className="space-y-2">
                    <Label>{t("dash.trade")}</Label>
                    <Select value={form.trade} onValueChange={(v) => setForm({ ...form, trade: v })}>
                      <SelectTrigger data-testid="profile-trade" className="rounded-lg"><SelectValue placeholder="—" /></SelectTrigger>
                      <SelectContent>{cats.map((c) => <SelectItem key={c} value={c}>{catName(c)}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2"><Label>{t("postJob.city")}</Label>
                    <Select value={form.city} onValueChange={(v) => setForm({ ...form, city: v })}>
                      <SelectTrigger data-testid="profile-city" className="rounded-lg"><SelectValue placeholder="—" /></SelectTrigger>
                      <SelectContent>{cities.map((c) => <SelectItem key={c} value={c}>{cityName(c)}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2"><Label>{t("dash.rate")}</Label><Input data-testid="profile-rate" type="number" value={form.hourly_rate} onChange={(e) => setForm({ ...form, hourly_rate: e.target.value })} className="rounded-lg" /></div>
                  <div className="space-y-2"><Label>{t("dash.phone")}</Label><Input data-testid="profile-phone" type="tel" inputMode="tel" maxLength={30} placeholder="+212 6 00 00 00 00" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value.replace(/[^0-9+()\-./ ]/g, "") })} className="rounded-lg" /><p className="text-xs text-[#8C7A70]">{t("dash.phoneHint")}</p></div>
                  <div className="space-y-2"><Label>{t("dash.bio")}</Label><Textarea data-testid="profile-bio" rows={3} value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })} className="rounded-lg" /></div>
                  <div className="space-y-2"><Label>{t("dash.skills")}</Label><Input data-testid="profile-skills" value={form.skills} onChange={(e) => setForm({ ...form, skills: e.target.value })} className="rounded-lg" /></div>
                  <div className="space-y-2">
                    <Label>{t("dash.avatar")}</Label>
                    <div className="flex items-center gap-3">
                      {form.avatar && <img src={form.avatar} alt="" className="w-12 h-12 rounded-full object-cover" />}
                      <label className="cursor-pointer text-sm text-[#C65338] font-semibold" data-testid="avatar-upload">
                        <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadFiles(e, "avatar")} />
                        {uploading ? t("portfolio.uploading") : t("portfolio.upload")}
                      </label>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>{t("portfolio.title")}</Label>
                    <div className="grid grid-cols-3 gap-2">
                      {form.portfolio.map((url, i) => (
                        <div key={i} className="relative group">
                          <img src={url} alt="" className="w-full h-16 object-cover rounded-lg" />
                          <button type="button" onClick={() => setForm((p) => ({ ...p, portfolio: p.portfolio.filter((_, x) => x !== i) }))} className="absolute -top-1 -right-1 bg-destructive text-white rounded-full p-0.5"><X className="w-3 h-3" /></button>
                        </div>
                      ))}
                      <label className="cursor-pointer h-16 rounded-lg border-2 border-dashed border-[#DCC2B3] flex items-center justify-center text-[#A89A90] hover:border-[#C65338]" data-testid="portfolio-upload">
                        <input type="file" accept="image/*" multiple className="hidden" onChange={(e) => uploadFiles(e, "portfolio")} />
                        <Upload className="w-5 h-5" />
                      </label>
                    </div>
                  </div>
                  <Button data-testid="save-profile-btn" type="submit" className="w-full rounded-full bg-[#C65338] hover:bg-[#A93F25] text-white">{t("dash.saveProfile")}</Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>
          {profile && (
            <div className="space-y-3">
              {profile.avatar && <img src={profile.avatar} alt="" className="w-28 h-28 sm:w-32 sm:h-32 rounded-2xl object-cover object-center border border-[#EBDDD3]" />}
              <div className="font-display font-bold text-lg text-[#2D231F]">{profile.name}</div>
              {profile.trade && <span className="inline-block px-2.5 py-1 rounded-full bg-[#C65338]/10 text-[#C65338] text-xs font-semibold">{catName(profile.trade)}</span>}
              {profile.city && <div className="text-sm text-[#8C7A70] flex items-center gap-1"><MapPin className="w-4 h-4" />{cityName(profile.city)}</div>}
              <p className="text-sm text-[#8C7A70]">{profile.bio || "—"}</p>
              <div className="flex items-center gap-4 text-sm text-[#8C7A70] pt-2">
                <StarRating value={profile.rating} />
                <span className="inline-flex items-center gap-1"><Briefcase className="w-4 h-4" />{profile.jobs_completed}</span>
              </div>
            </div>
          )}
        </div>
        <CreditsPanel />
      </div>}

      {view === "offers" && <div>
        {invited.length > 0 && (
          <div data-testid="invited-box" className="mb-8 rounded-2xl border-2 border-[#E69625] bg-[#FFF4E0] p-5">
            <h2 className="font-display text-xl font-bold text-[#2D231F]">{t("inv.title")}</h2>
            <p className="text-sm text-[#5A4B45] mb-3">{t("inv.sub")}</p>
            <div className="space-y-2">
              {invited.map((j) => (
                <div key={j._id} className="flex items-center justify-between gap-3 rounded-xl bg-white border border-[#EBDDD3] px-4 py-3">
                  <div className="min-w-0"><div className="font-semibold text-[#2D231F] truncate">{j.title}</div><div className="text-xs text-[#8C7A70]">{j.customer_name} · {cityName(j.city)}</div></div>
                  <Link to={`/jobs/${j._id}`}><Button size="sm" data-testid={`invited-${j._id}`} className="rounded-full bg-[#C65338] hover:bg-[#A93F25] text-white">{t("inv.view")}</Button></Link>
                </div>
              ))}
            </div>
          </div>
        )}
        <h2 className="font-display text-2xl font-bold text-[#2D231F] mb-5">{t("dash.myApplications")}</h2>
        {apps.length === 0 ? (
          <p className="text-[#A89A90] py-16 text-center">{t("dash.noApps")}</p>
        ) : (
          <div className="space-y-4">
            {apps.map((a) => (
              <div key={a._id} className="rounded-xl border border-[#EBDDD3] bg-white p-5" data-testid={`my-app-${a._id}`}>
                <div className="flex items-center justify-between gap-2">
                  <Link to={a.job ? `/jobs/${a.job._id}` : "#"} className="font-semibold text-[#2D231F] hover:text-[#C65338]">{a.job ? jt(a.job, "title") : "—"}</Link>
                  <div className="flex items-center gap-2">
                    {a.status && <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${a.status === "accepted" ? "bg-[#5C6646]/10 text-[#5C6646]" : a.status === "rejected" ? "bg-[#F2E8DF] text-[#A89A90]" : "bg-[#E69625]/15 text-[#B27A1E]"}`}>{a.status}</span>}
                    {a.price != null && <span className="font-bold text-[#C65338]">{money(a.price)}</span>}
                  </div>
                </div>
                <p className="text-sm text-[#8C7A70] mt-2">{a.message}</p>
                <div className="flex items-center gap-2 mt-3">
                  {a.job && <Button size="sm" variant="outline" data-testid={`app-msg-${a._id}`} onClick={() => setChat({ jobId: a.job._id, name: a.job.customer_name })} className="rounded-full gap-1.5"><MessageSquare className="w-4 h-4" />{t("dash.message")}</Button>}
                  {a.status === "accepted" && a.customer_phone && <a href={`tel:${a.customer_phone.replace(/[^0-9+]/g, "")}`} data-testid={`customer-phone-${a._id}`} title={t("prof.customerPhone")} className="inline-flex items-center gap-1.5 rounded-full bg-[#5C6646] text-white text-sm font-bold px-4 h-9"><Phone className="w-4 h-4" />{a.customer_phone}</a>}
                  {a.job && <ReportDialog jobId={a.job._id} reported={reportedJobs.includes(a.job._id)} onDone={(jid) => setReportedJobs((p) => [...p, jid])} />}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>}
      {chat && <ChatDialog open={!!chat} onOpenChange={(o) => !o && setChat(null)} jobId={chat.jobId} craftsmanUserId={user._id} title={chat.name} />}
    </div>
  );
}

const LANG_NAMES = { de: "Deutsch", en: "English", fr: "Français", ar: "العربية" };

function EmailLangSetting() {
  const { t } = useI18n();
  const { user, updateLang } = useAuth();
  const [saving, setSaving] = useState(false);
  const change = async (l) => {
    setSaving(true);
    try {
      await updateLang(l);
      toast.success(t("settings.langSaved"));
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  return (
    <div className="bg-white rounded-2xl border border-[#EBDDD3] p-5 mb-8 flex flex-col sm:flex-row sm:items-center gap-3 sm:justify-between" data-testid="email-lang-setting">
      <div>
        <div className="text-sm font-semibold text-[#3A2E28]">{t("settings.emailLang")}</div>
        <p className="text-xs text-[#A89A90]">{t("settings.emailLangHint")}</p>
      </div>
      <Select value={user?.lang || "fr"} onValueChange={change} disabled={saving}>
        <SelectTrigger data-testid="email-lang-select" className="rounded-lg w-full sm:w-48">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {Object.keys(LANG_NAMES).map((l) => (
            <SelectItem key={l} value={l} data-testid={`email-lang-${l}`}>{LANG_NAMES[l]}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

function DeleteAccount({ role }) {
  const { t } = useI18n();
  const { setUser } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    try {
      await api.post("/auth/delete-account", { password: pw });
      toast.success(t("acct.done"));
      setUser(false); nav("/");
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };
  return (
    <div className="mt-12 rounded-2xl border-2 border-[#B0413E]/30 bg-[#B0413E]/5 p-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-testid="account-zone">
      <div>
        <h2 className="font-display text-xl font-bold text-[#2D231F]">{t("acct.zone")}</h2>
        <p className="text-sm text-[#5A4B45] mt-1 max-w-xl">{t("acct.zoneHint")}</p>
      </div>
      <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setPw(""); }}>
        <DialogTrigger asChild><Button variant="outline" data-testid="delete-account-btn" className="rounded-full gap-2 h-11 px-6 font-bold border-2 border-[#B0413E] text-[#B0413E] hover:bg-[#B0413E] hover:text-white shrink-0"><Trash2 className="w-4 h-4" />{t("acct.delete")}</Button></DialogTrigger>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-display text-[#B0413E]">{t("acct.title")}</DialogTitle></DialogHeader>
          <div className="space-y-3 text-sm text-[#5A4B45]">
            <p className="font-semibold">{t("acct.intro")}</p>
            <p>{role === "craftsman" ? t("acct.pCraftsman") : t("acct.pCustomer")}</p>
            <p>{t("acct.pLaw")}</p>
            <p>{t("acct.pActive")}</p>
            <div className="space-y-2 pt-1">
              <Label>{t("acct.password")}</Label>
              <Input data-testid="delete-account-password" type="password" autoComplete="current-password" value={pw} onChange={(e) => setPw(e.target.value)} className="rounded-lg" />
            </div>
            <div className="flex gap-2 justify-end pt-2">
              <Button variant="outline" className="rounded-full" onClick={() => setOpen(false)}>{t("acct.cancel")}</Button>
              <Button data-testid="delete-account-confirm" disabled={!pw || busy} onClick={go} className="rounded-full bg-[#B0413E] hover:bg-[#8E2F2C] text-white">{t("acct.confirm")}</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function CustomerProfile() {
  const { t, cityName } = useI18n();
  const { user, setUser } = useAuth();
  const [cities, setCities] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ name: user?.name || "", city: user?.city || "", phone: user?.phone || "", avatar: user?.avatar || "" });
  useEffect(() => { api.get("/cities").then((r) => setCities(r.data)); }, []);
  const upload = async (e) => {
    const f = (e.target.files || [])[0];
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((p) => ({ ...p, avatar: data.url }));
    } catch (err) { toast.error(apiError(err.response?.data?.detail) || "Upload failed"); }
    finally { setUploading(false); }
  };
  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.put("/customers/me", { ...form, name: form.name.trim(), phone: form.phone.trim() });
      setUser(data);
      toast.success(t("dash.saved"));
    } catch (err) { toast.error(apiError(err.response?.data?.detail) || err.message); }
    finally { setSaving(false); }
  };
  return (
    <div>
      <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tight text-[#2D231F] mb-8">{t("nav.myProfile")}</h1>
      <form onSubmit={save} className="rounded-2xl border border-[#EBDDD3] bg-white p-6 max-w-2xl space-y-5" data-testid="customer-profile-card">
        <div className="flex items-center gap-4">
          {form.avatar
            ? <img src={form.avatar} alt="" className="w-28 h-28 sm:w-32 sm:h-32 rounded-2xl object-cover object-center border border-[#EBDDD3]" />
            : <span className="w-28 h-28 sm:w-32 sm:h-32 rounded-2xl bg-[#C65338]/10 text-[#C65338] font-display font-black text-4xl flex items-center justify-center">{(form.name || "?").split(" ").map((p) => p[0]).slice(0, 2).join("").toUpperCase()}</span>}
          <div>
            <Label>{t("dash.avatar")} <span className="text-xs text-[#A89A90] font-normal">({t("prof.optional")})</span></Label>
            <label className="block mt-1 cursor-pointer text-sm text-[#C65338] font-semibold" data-testid="customer-avatar-upload">
              <input type="file" accept="image/*" className="hidden" onChange={upload} />
              {uploading ? t("portfolio.uploading") : t("portfolio.upload")}
            </label>
            {form.avatar && <button type="button" onClick={() => setForm({ ...form, avatar: "" })} className="text-xs text-[#8C7A70] underline mt-1">×</button>}
          </div>
        </div>
        <div className="space-y-2"><Label>{t("prof.name")}</Label><Input data-testid="customer-name" required maxLength={80} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-lg" /></div>
        <div className="space-y-2"><Label>{t("postJob.city")} <span className="text-xs text-[#A89A90] font-normal">({t("prof.optional")})</span></Label>
          <Select value={form.city} onValueChange={(v) => setForm({ ...form, city: v })}>
            <SelectTrigger data-testid="customer-city" className="rounded-lg"><SelectValue placeholder="—" /></SelectTrigger>
            <SelectContent>{cities.map((c) => <SelectItem key={c} value={c}>{cityName(c)}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="space-y-2"><Label>{t("dash.phone")} <span className="text-xs text-[#A89A90] font-normal">({t("prof.optional")})</span></Label>
          <Input data-testid="customer-phone" type="tel" inputMode="tel" maxLength={30} placeholder="+212 6 00 00 00 00" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value.replace(/[^0-9+()\-./ ]/g, "") })} className="rounded-lg" />
          <p className="text-xs text-[#8C7A70]">{t("prof.phoneHint")}</p>
        </div>
        <Button type="submit" data-testid="customer-profile-save" disabled={saving || !form.name.trim()} className="rounded-full bg-[#C65338] hover:bg-[#A93F25] text-white px-8">{t("prof.save")}</Button>
      </form>
      <div className="mt-8 max-w-2xl"><EmailLangSetting /></div>
      <DeleteAccount role="customer" />
    </div>
  );
}

export default function Dashboard({ view = "profile" }) {
  const { user } = useAuth();
  const nav = useNavigate();
  useEffect(() => {
    if (user === false) nav(`/login?next=${encodeURIComponent(window.location.pathname + window.location.search)}`);
    else if (user && user.role === "admin") nav("/admin");
  }, [user, nav]);
  if (!user || user === false) return <div className="py-24 text-center text-[#A89A90]">…</div>;
  return (
    <div className="max-w-6xl mx-auto px-5 sm:px-8 py-14">
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
        {user.role === "craftsman" && <EmailLangSetting />}
        {user.role === "craftsman" ? <CraftsmanDashboard view={view} /> : view === "myprofile" ? <CustomerProfile /> : <CustomerDashboard />}
        {user.role === "craftsman" && view !== "offers" && <DeleteAccount role="craftsman" />}
      </motion.div>
    </div>
  );
}